import {
  ChangeEventHandler,
  Dispatch,
  FC,
  JSX,
  KeyboardEventHandler,
  MouseEventHandler,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {styled} from 'styled-components';

import {startOfLocalDay, startOfUtcDay} from '@shared/lib/utils/date_utils';
import {
  utcDate,
  localeDate,
  localeDateString,
  localeTimeString,
  utcDateString,
  utcTimeString,
} from '@shared/lib/utils/time_format';

import {withThemeOverride} from '@shared_web/components/core/_base';
import {Calendar} from '@shared_web/components/core/calendar';
import {Input} from '@shared_web/components/core/input';
import {TimePicker} from '@shared_web/components/core/time_picker';
import {EmptyFragment} from '@shared_web/lib/react';
import {optionalRaw} from '@shared_web/lib/styled_utils';
import {useClickOutside} from '@shared_web/lib/use_click_outside';

interface DateTimeInputProps {
  ts?: number;
  minTs?: number;
  maxTs?: number;
  syncState?: Dispatch<number>;
  label?: string | JSX.Element;
  autoFocus?: boolean;
  noTimePicker?: boolean;
  utc?: boolean;
  dateAndTimeJoiner?: string;
  denseUi?: boolean;
  alignPopupRight?: boolean;
}

const SPACE_BETWEEN_INPUT_AND_CALENDAR = 8;

const _DateTimeInput: FC<DateTimeInputProps> = props => {
  const {
    ts,
    minTs,
    maxTs,
    syncState,
    label,
    autoFocus,
    noTimePicker,
    utc,
    dateAndTimeJoiner = ' ',
    denseUi = false,
    alignPopupRight = false,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const stringToTs = useCallback(
    (str: string, opts: {includeTime?: boolean}): number | undefined => {
      const {includeTime} = opts;
      if (str === '') {
        return undefined;
      }

      if (!includeTime) {
        return utc ? utcDate(str).getTime() : localeDate(str).getTime();
      }

      const [dateStr, timeStr] = str.split(dateAndTimeJoiner);
      if (dateStr === undefined) {
        return undefined;
      }
      const date = utc ? utcDate(dateStr) : localeDate(dateStr);
      if (Number.isNaN(date.getTime())) {
        return undefined;
      }

      const time = timeStr?.split(':').map(Number) ?? undefined;
      if (time === undefined || time.length === 0) {
        console.log('res5', date.getTime());
        return date.getTime();
      }
      if (utc) {
        date.setUTCHours(time[0] ?? 0);
        date.setUTCMinutes(time[1] ?? 0);
        date.setUTCSeconds(time[2] ?? 0);
        date.setUTCMilliseconds(0);
      } else {
        date.setHours(time[0] ?? 0);
        date.setMinutes(time[1] ?? 0);
        date.setSeconds(time[2] ?? 0);
        date.setMilliseconds(0);
      }
      return date.getTime();
    },
    [dateAndTimeJoiner, utc]
  );

  const tsToString = useCallback(
    (ts: number | undefined, opts: {includeTime?: boolean}): string => {
      if (ts === undefined) {
        return '';
      }

      const {includeTime} = opts;
      const dateStr = utc ? utcDateString(new Date(ts)) : localeDateString(new Date(ts));
      if (!includeTime) {
        return dateStr;
      }

      const timeStr = utc ? utcTimeString(new Date(ts)) : localeTimeString(new Date(ts));
      return [dateStr, dateAndTimeJoiner, timeStr].join('');
    },
    [dateAndTimeJoiner, utc]
  );

  const [inputText, setInputText] = useState(
    ts !== undefined ? tsToString(ts, {includeTime: !noTimePicker}) : ''
  );

  // Refresh input text when ts changes
  useEffect(() => {
    if (ts === undefined || Number.isNaN(ts)) {
      return;
    }
    setInputText(tsToString(ts, {includeTime: !noTimePicker}));
  }, [ts, noTimePicker, tsToString]);

  const [calendarShown, setCalendarShown] = useState(false);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<Date | undefined>();
  const [calendarOffset, setCalendarOffset] = useState(0);
  const isHoveringCalendar = useRef(false);

  useEffect(() => {
    if (ts !== undefined) {
      setCalendarDate(new Date(ts));
    }
  }, [ts]);

  const bail = useCallback(() => {
    setCalendarShown(false);
    setSelectedDay(undefined);
    inputRef.current?.blur();
  }, []);
  useClickOutside(wrapperRef, bail);

  // Compute the `calendarOffset` which should be the height of the elements before the calendar div
  useLayoutEffect(() => {
    if (!wrapperRef.current) {
      return;
    }
    const {children} = wrapperRef.current;
    let heightSum = 0;
    for (let i = 0; i < children.length; i++) {
      const element = children.item(i);
      if (!element) {
        continue;
      }
      // eslint-disable-next-line unicorn/consistent-function-scoping
      const cssValue = (css: string): string | undefined =>
        document.defaultView?.getComputedStyle(element).getPropertyValue(css);
      // eslint-disable-next-line unicorn/consistent-function-scoping
      const cssPx = (css: string): number => {
        const computed = cssValue(css);
        const pxSuffix = 'px';
        if (computed?.endsWith(pxSuffix)) {
          const pxValue = computed.slice(0, -pxSuffix.length);
          const pxInt = Number.parseFloat(pxValue);
          return Number.isNaN(pxInt) ? 0 : pxInt;
        }
        return 0;
      };
      if (['relative', 'static'].includes(cssValue('position') ?? '')) {
        heightSum +=
          element.getBoundingClientRect().height + cssPx('margin-top') + cssPx('margin-bottom');
      }
      setCalendarOffset(heightSum + SPACE_BETWEEN_INPUT_AND_CALENDAR);
    }
  }, []);

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    e => {
      const text = e.currentTarget.value;
      setInputText(text);
      const newTs = stringToTs(text, {includeTime: !noTimePicker});
      if (newTs !== undefined) {
        syncState?.(newTs);
      }
    },
    [stringToTs, syncState, noTimePicker]
  );

  const handlePreviousMonthClick = useCallback(() => {
    setCalendarDate(currentDate => new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  }, []);
  const handleNextMonthClick = useCallback(() => {
    setCalendarDate(currentDate => new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  }, []);

  const handleDayClick = useCallback<MouseEventHandler>(
    evt => {
      const date = new Date(Number.parseFloat(evt.currentTarget.getAttribute('data-ts') ?? ''));
      if (Number.isNaN(date.getTime())) {
        return;
      }
      if (noTimePicker) {
        syncState?.(date.getTime());
        setCalendarShown(false);
      } else {
        setTimeout(() => setSelectedDay(date));
      }
    },
    [noTimePicker, syncState]
  );

  const handleTimeClick = useCallback(
    (hour: number, minute: number) => {
      if (!selectedDay) {
        return;
      }
      const date = new Date(selectedDay.getTime());
      if (utc) {
        date.setUTCHours(hour);
        date.setUTCMinutes(minute);
        date.setUTCSeconds(0);
        date.setUTCMilliseconds(0);
      } else {
        date.setHours(hour);
        date.setMinutes(minute);
        date.setSeconds(0);
        date.setMilliseconds(0);
      }
      const ts = date.getTime();
      setInputText(tsToString(ts, {includeTime: !noTimePicker}));
      setCalendarShown(false);
      syncState?.(ts);
    },
    [selectedDay, syncState, noTimePicker, tsToString, utc]
  );

  const handleFocus = useCallback(() => setCalendarShown(true), []);
  const handleBlur = useCallback(() => {
    if (Math.random() > 0) {
      return;
    }
    if (!isHoveringCalendar.current) {
      setCalendarShown(false);
      if (ts !== undefined) {
        setInputText(tsToString(ts, {includeTime: !noTimePicker}));
      }
    }
  }, [ts, noTimePicker, tsToString]);

  const handleMouseEnter = useCallback(() => {
    isHoveringCalendar.current = true;
  }, []);
  const handleMouseLeave = useCallback(() => {
    isHoveringCalendar.current = false;
  }, []);

  const handleKeyDown = useCallback<KeyboardEventHandler>(
    e => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        bail();
      }
    },
    [bail]
  );

  const renderCell = useCallback(
    (date: Date) => {
      const dateTs = date.getTime();
      const today = dateTs === (utc ? startOfUtcDay().getTime() : startOfLocalDay().getTime());
      const disabled =
        (minTs !== undefined && dateTs < minTs) || (maxTs !== undefined && dateTs > maxTs);
      const selected =
        ts !== undefined &&
        (utc ? startOfUtcDay(new Date(ts)).getTime() : startOfLocalDay(new Date(ts)).getTime()) ===
          dateTs;
      return (
        <CalendarCell
          key={date.getTime()}
          data-ts={date.getTime()}
          onClick={handleDayClick}
          $disabled={disabled}
          $selected={selected}
          $denseUi={denseUi}
        >
          <CalendarCellDay>
            <CalendarCellDayNumber $today={today}>{date.getDate()}</CalendarCellDayNumber>
          </CalendarCellDay>
        </CalendarCell>
      );
    },
    [handleDayClick, maxTs, minTs, ts, denseUi, utc]
  );

  return (
    <Wrapper ref={wrapperRef}>
      <StyledInput
        ref={inputRef}
        value={inputText}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        label={label}
        autoFocus={autoFocus}
        autoComplete="off"
        autoCapitalize="off"
        spellCheck={false}
        $calendarShown={calendarShown}
      />
      {calendarShown ? (
        <Popup
          $offset={calendarOffset}
          $denseUi={denseUi}
          $alignPopupRight={alignPopupRight}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {selectedDay ? (
            <TimePicker
              header={selectedDay.toLocaleDateString(undefined, {
                dateStyle: 'full',
                timeZone: utc ? 'UTC' : undefined,
              })}
              onClick={handleTimeClick}
              denseUi={denseUi}
            />
          ) : (
            <Calendar
              month={calendarDate.getMonth()}
              year={calendarDate.getFullYear()}
              renderCell={renderCell}
              onPreviousClick={handlePreviousMonthClick}
              onNextClick={handleNextMonthClick}
              utc={utc}
              denseUi={denseUi}
            />
          )}
        </Popup>
      ) : (
        EmptyFragment
      )}
    </Wrapper>
  );
};

export const DateTimeInput = withThemeOverride(_DateTimeInput);
DateTimeInput.displayName = 'DateTimeInput';

const Wrapper = styled.div`
  position: relative;
`;

const StyledInput = styled(Input)<{$calendarShown: boolean}>`
  width: 100%;
`;

const Popup = styled.div<{
  $offset: number;
  $denseUi: boolean;
  $alignPopupRight: boolean;
}>`
  position: absolute;
  top: ${p => p.$offset}px;
  ${p => (p.$alignPopupRight ? 'right: 0;' : 'left: 0;')}
  overflow: auto;
  z-index: 5;

  padding: ${p => (p.$denseUi ? '10px 12px 12px 12px' : '20px 24px 24px 24px')};
  border: solid 2px ${p => p.theme.input?.focusBorderColor ?? p.theme.input?.borderColor};
  border-radius: ${p => (p.$denseUi ? '8px' : '16px')};
  ${p => optionalRaw(p.theme.main?.textColor, val => `box-shadow: 0px 0px 20px -10px ${val};`)};
  background-color: ${p => p.theme.main?.backgroundColor};
`;

const CalendarCell = styled.div<{
  $disabled: boolean;
  $selected: boolean;
  $denseUi: boolean;
}>`
  display: flex;
  flex-direction: column;
  width: ${p => (p.$denseUi ? '40px' : '48px')};
  height: ${p => (p.$denseUi ? '44px' : '52px')};
  border-radius: ${p => (p.$denseUi ? '4px' : '6px')};
  margin: ${p => (p.$denseUi ? '2px' : '3px')};
  background: ${p => p.theme.main?.textColor}06;
  color: ${p => (p.$selected ? p.theme.main?.accentColor : `${p.theme.main?.textColor}60`)};
  border: solid 2px ${p => (p.$selected ? p.theme.main?.accentColor : `${p.theme.main?.textColor}10`)};
  cursor: ${p => (p.$disabled ? 'default' : 'pointer')};
  pointer-events: ${p => (p.$disabled ? 'none' : 'all')};
  opacity: ${p => (p.$disabled ? '0.5' : '1')};
  &:hover {
    border: solid 2px ${p => p.theme.main?.accentColor};
    color: ${p => p.theme.main?.accentColor};
  }
`;

const CalendarCellDay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
`;
const CalendarCellDayNumber = styled.div<{
  $today: boolean;
}>`
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  ${p => (p.$today ? `background-color: ${p.theme.main?.textColor}10;` : false)};
`;
